import React, { useState, useEffect, useCallback } from 'react';
import { ConversationList } from './components/ConversationList';
import { ConversationThread } from './components/ConversationThread';
import { NewConversationModal } from './components/NewConversationModal';
import { smsAPI } from './api/sms.api';
import { useSMSLiveUpdates } from './hooks/useSMSLiveUpdates';
import type { Conversation, ConversationDetail, Message } from './types/sms.types';
import './styles/SMSTab.css';

export const SMSTab: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [currentConversation, setCurrentConversation] = useState<ConversationDetail | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);

  // Fetch conversations list
  const fetchConversations = useCallback(async () => {
    try {
      setLoadingList(true);
      const response = await smsAPI.getConversations({ search: searchQuery, limit: 100 });
      if (response.success) {
        setConversations(response.data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoadingList(false);
    }
  }, [searchQuery]);

  // Fetch single conversation thread
  // silent=true means don't show loading spinner (used for background refresh)
  const fetchConversation = useCallback(async (phoneNumber: string, silent = false) => {
    try {
      if (!silent) {
        setLoadingThread(true);
      }
      const response = await smsAPI.getConversation(phoneNumber);
      if (response.success) {
        setCurrentConversation(response.data);
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
    } finally {
      if (!silent) {
        setLoadingThread(false);
      }
    }
  }, []);

  // Load conversation when selected
  useEffect(() => {
    if (selectedPhone) {
      fetchConversation(selectedPhone);
    } else {
      setCurrentConversation(null);
    }
  }, [selectedPhone, fetchConversation]);

  // Fetch conversations on mount and when search changes (debounced)
  useEffect(() => {
    // No debounce on initial load (empty query), debounce on search changes
    if (searchQuery === '') {
      fetchConversations();
      return;
    }
    const timer = setTimeout(() => {
      fetchConversations();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle selecting a conversation
  const handleSelectConversation = async (phoneNumber: string) => {
    setSelectedPhone(phoneNumber);

    // Find the conversation to check if it has unread messages
    const conversation = conversations.find(c => c.phoneNumber === phoneNumber);
    if (conversation && conversation.unreadCount > 0) {
      try {
        // Mark conversation as read on the server
        await smsAPI.markConversationRead(phoneNumber);

        // Update local state to clear the unread count
        setConversations(prev =>
          prev.map(conv =>
            conv.phoneNumber === phoneNumber
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );
      } catch (error) {
        console.error('Error marking conversation as read:', error);
        // Non-blocking - don't throw, just log
      }
    }
  };

  // Handle sending a message in existing conversation (with optimistic update)
  const handleSendMessage = async (message: string) => {
    if (!selectedPhone || !currentConversation) return;

    // Create optimistic message
    // Create ISO-like string using LOCAL time values (not UTC)
    const now = new Date();
    const pad = (n: number, len = 2) => String(n).padStart(len, '0');
    const localTimestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.${pad(now.getMilliseconds(), 3)}`;

    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`,
      phoneNumber: selectedPhone,
      message,
      direction: 'outbound',
      timestamp: localTimestamp,
      status: 'pending',
      affaireId: currentConversation?.contact?.affaireId || null,
      senderName: null,
      createdByName: null,
      isProcessed: null
    };

    // Optimistically add the message to the conversation
    setCurrentConversation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: [...prev.messages, optimisticMessage]
      };
    });

    // Also update the conversation list preview
    setConversations(prev =>
      prev.map(conv =>
        conv.phoneNumber === selectedPhone
          ? {
              ...conv,
              lastMessage: message,
              lastMessageTime: optimisticMessage.timestamp,
              lastDirection: 'outbound'
            }
          : conv
      )
    );

    try {
      await smsAPI.sendSMS({
        phoneNumber: selectedPhone,
        message,
        metadata: currentConversation?.contact?.affaireId
          ? { affaireId: currentConversation.contact.affaireId }
          : undefined
      });

      // Refresh to get the actual message from server (silent=true to keep scroll position)
      await fetchConversation(selectedPhone, true);
      await fetchConversations();
    } catch (error) {
      console.error('Error sending SMS:', error);

      // Remove the optimistic message on error
      setCurrentConversation(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.filter(m => m.id !== optimisticMessage.id)
        };
      });

      throw error;
    }
  };

  // Handle sending a new conversation
  const handleNewConversation = async (phoneNumber: string, message: string) => {
    try {
      await smsAPI.sendSMS({
        phoneNumber,
        message
      });

      // Select the new conversation
      setSelectedPhone(phoneNumber);
      // Refresh list
      await fetchConversations();
    } catch (error) {
      console.error('Error sending new SMS:', error);
      throw error;
    }
  };

  // Handle live status updates from gateway (when SMS is actually sent)
  const handleStatusUpdated = useCallback((event: { id: string; status: 'sent' | 'failed'; updatedAt: string }) => {
    // Update the message status in current conversation
    setCurrentConversation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: prev.messages.map(msg =>
          msg.id === event.id
            ? { ...msg, status: event.status }
            : msg
        )
      };
    });
  }, []);

  // Handle live received messages
  const handleMessageReceived = useCallback(() => {
    // Refresh conversations list to show new message
    fetchConversations();
    // If we have a selected conversation, refresh it too
    if (selectedPhone) {
      fetchConversation(selectedPhone, true);
    }
  }, [fetchConversations, fetchConversation, selectedPhone]);

  // Use WebSocket for live updates
  useSMSLiveUpdates({
    onMessageReceived: handleMessageReceived,
    onStatusUpdated: handleStatusUpdated,
    selectedPhone
  });

  return (
    <div className="sms-tab">
      <div className="sms-container">
        <ConversationList
          conversations={conversations}
          selectedPhone={selectedPhone}
          onSelect={handleSelectConversation}
          onNewConversation={() => setShowNewModal(true)}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          loading={loadingList}
        />
        <ConversationThread
          conversation={currentConversation}
          onSendMessage={handleSendMessage}
          onConversationUpdated={() => {
            fetchConversations();
            if (selectedPhone) fetchConversation(selectedPhone, true);
          }}
          loading={loadingThread}
        />
      </div>

      <NewConversationModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSend={handleNewConversation}
      />
    </div>
  );
};

export default SMSTab;
